import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  AccountTree as TreeIcon,
  People as PeopleIcon,
  Photo as PhotoIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/**
 * OnboardingModal Component
 *
 * Multi-step onboarding flow for new users
 *
 * Steps:
 * 1. Welcome to Martz Family Genealogy
 * 2. Explore the Family Tree
 * 3. Add Family Members
 * 4. Share Photos and Memories
 * 5. Invite Family Members
 *
 * Features:
 * - Step-by-step introduction
 * - Visual icons for each step
 * - "Don't show again" option
 * - Navigation to key features
 *
 * @component
 * @param {OnboardingModalProps} props - Component props
 * @returns {JSX.Element} Rendered onboarding modal
 */
interface OnboardingModalProps {
  open: boolean;
  onClose: (dontShowAgain: boolean) => void;
}

export const OnboardingModal: React.FC<OnboardingModalProps> = ({ open, onClose }) => {
  // ===== State =====
  const [activeStep, setActiveStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const navigate = useNavigate();

  // ===== Constants =====
  const steps = [
    {
      label: 'Welcome!',
      icon: <TreeIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      title: 'Welcome to Martz Family Genealogy',
      description: 'Your family\'s story starts here. This platform helps you build, preserve, and share your family history with loved ones.',
      action: null,
    },
    {
      label: 'Family Tree',
      icon: <TreeIcon sx={{ fontSize: 48, color: 'success.main' }} />,
      title: 'Explore the Family Tree',
      description: 'Navigate through generations and discover connections. View your family\'s lineage in an interactive tree visualization.',
      action: {
        label: 'View Family Tree',
        onClick: () => {
          handleClose(false);
          navigate('/tree');
        },
      },
    },
    {
      label: 'Add People',
      icon: <PeopleIcon sx={{ fontSize: 48, color: 'info.main' }} />,
      title: 'Add Family Members',
      description: 'Build your family tree by adding people and their relationships. Include biographical details, dates, and locations.',
      action: {
        label: 'Browse People',
        onClick: () => {
          handleClose(false);
          navigate('/people');
        },
      },
    },
    {
      label: 'Photos',
      icon: <PhotoIcon sx={{ fontSize: 48, color: 'warning.main' }} />,
      title: 'Share Photos & Memories',
      description: 'Upload photos and tag family members. Preserve precious memories for future generations.',
      action: {
        label: 'Add Photos',
        onClick: () => {
          handleClose(false);
          navigate('/people');
        },
      },
    },
    {
      label: 'Invite',
      icon: <ShareIcon sx={{ fontSize: 48, color: 'secondary.main' }} />,
      title: 'Invite Family Members',
      description: 'Collaborate with family members to build a comprehensive family history together. Share the link and grow your tree!',
      action: null,
    },
  ];

  // ===== Event Handlers =====
  const handleNext = () => {
    setActiveStep(prev => Math.min(prev + 1, steps.length - 1));
  };

  const handleBack = () => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  const handleClose = (dontShow: boolean) => {
    onClose(dontShow);
  };

  const handleFinish = () => {
    handleClose(dontShowAgain);
  };

  // ===== Render =====
  return (
    <Dialog
      open={open}
      onClose={() => handleClose(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Getting Started with Martz Family Genealogy
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel>{step.label}</StepLabel>
              <StepContent>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  {step.icon}
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {step.description}
                  </Typography>
                  {step.action && (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={step.action.onClick}
                      sx={{ mt: 1 }}
                    >
                      {step.action.label}
                    </Button>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button
                    disabled={index === 0}
                    onClick={handleBack}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={index === steps.length - 1 ? handleFinish : handleNext}
                  >
                    {index === steps.length - 1 ? 'Get Started' : 'Next'}
                  </Button>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
            />
          }
          label="Don't show this again"
        />
        <Button onClick={() => handleClose(dontShowAgain)}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
